import clsx from 'clsx';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import {
  FiAlertTriangle,
  FiAtSign,
  FiBriefcase,
  FiCalendar,
  FiCheckSquare,
  FiCloud,
  FiCoffee,
  FiCreditCard,
  FiDollarSign,
  FiGift,
  FiMail,
  FiStar,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { DeclineReasonModal } from '@/components/EventSidePanel/DeclineReasonModal';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { Tile } from '@/components/Tile/Tile';
import { useInviteExpiry } from '@/hooks/useInviteExpiry';
import { useInviteResponse } from '@/hooks/useInviteResponse';
import { useNotificationsPanel } from '@/hooks/useNotificationsPanel';
import { EVENT_ID_SEARCH_PARAM } from '@/pages/EventsPage/event-deep-link';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { useChats } from '@/store/chats/chats.store';
import { guestsLabel } from '@/store/events/events.dto';
import { taskHref } from '@/utils/task-href';

import { useEventExistsQuery, useTaskLinkQuery } from './notification-queries';
import styles from './NotificationCard.module.scss';
import type { NotificationBatch, NotificationItem, NotificationKind } from './types';

const KIND_ICON: Record<NotificationKind, ReactNode> = {
  task: <FiCheckSquare />,
  event: <FiCalendar />,
  vessel: <FiCloud />,
  alert: <FiAlertTriangle />,
  message: <FiMail />,
  mention: <FiAtSign />,
  service: <FiBriefcase />,
  payment: <FiDollarSign />,
  subscription: <FiCreditCard />,
  referral: <FiGift />,
  menu: <FiCoffee />,
};

interface NotificationCardProps {
  batch: NotificationBatch;
  onMarkRead: (ids: string[]) => void;
  onMarkUnread: (ids: string[]) => void;
  pinned?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  'co-owner': 'Co-owner',
  guest: 'Guest',
  captain: 'Captain',
  chef: 'Chef',
  mate: 'Mate',
  steward: 'Steward',
  'service-provider': 'Service Provider',
};

const formatSenderRole = (role: string): string =>
  ROLE_LABELS[role] ?? role.charAt(0).toUpperCase() + role.slice(1);

export const NotificationCard = ({
  batch,
  onMarkRead,
  onMarkUnread,
  pinned,
}: NotificationCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const { kind, title, danger, items, senderRole } = batch;
  const primary = items[0];
  const isBatch = items.length > 1;
  const unreadIds = items.filter((i) => !i.read).map((i) => i.id);
  const hasUnread = unreadIds.length > 0;
  const allIds = items.map((i) => i.id);

  return (
    <Tile className={clsx(styles.card, pinned && styles.cardPinned, !hasUnread && styles.cardRead)}>
      <div className={styles.row}>
        <div className={clsx(styles.icon, danger && styles.danger)}>{KIND_ICON[kind]}</div>

        <div className={styles.body}>
          <div className={styles.title}>
            <span>{title}</span>
            {isBatch && <span className={styles.count}>×{items.length}</span>}
          </div>
          {primary.description && (
            <div className={styles.description}>
              <NotificationText item={primary} text={primary.description} />
            </div>
          )}
          {primary.chatRoom && <ChatRoomLink room={primary.chatRoom} href={primary.link} />}
          {pinned && senderRole && (
            <div className={styles.senderTag}>
              <FiStar />
              <span>From {formatSenderRole(senderRole)}</span>
            </div>
          )}
          {primary.invite && <InviteActions invite={primary.invite} />}
        </div>

        <span className={styles.time}>{primary.time}</span>

        <button
          type="button"
          className={styles.readToggle}
          onClick={(e) => {
            e.stopPropagation();
            if (hasUnread) onMarkRead(unreadIds);
            else onMarkUnread(allIds);
          }}
          aria-label={
            hasUnread
              ? isBatch
                ? 'Mark All as Read'
                : 'Mark as Read'
              : isBatch
                ? 'Mark All as Unread'
                : 'Mark as Unread'
          }
          title={hasUnread ? 'Mark as Read' : 'Mark as Unread'}
        >
          <span className={clsx(styles.readToggleDot, !hasUnread && styles.readToggleDotEmpty)} />
        </button>
      </div>

      {isBatch && (
        <>
          <button
            type="button"
            className={clsx(styles.expander, expanded && styles.expanderOpen)}
            onClick={() => setExpanded((v) => !v)}
          >
            <BiChevronDown className={styles.expanderIcon} />
            {expanded ? 'Hide' : 'Show'} {items.length} items
          </button>
          {expanded && (
            <ul className={styles.children}>
              {items.map((item) => (
                <li key={item.id} className={clsx(styles.child, item.read && styles.childRead)}>
                  <span className={styles.childText}>
                    <NotificationText item={item} text={item.description || item.title} />
                  </span>
                  <span className={styles.childTime}>{item.time}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Tile>
  );
};

/**
 * The notification's text, linked to whatever it points at.
 *
 * The notification service sends a `link` with the event, but it only knows the record's id —
 * so for the three targets the app can check, the frontend keeps resolving them itself: a task
 * opens on the list that owns it (which depends on its equipment), and an event or menu that is
 * gone, or that the reader may not open, stays plain text instead of becoming a dead link.
 * Everything else the server links (payments today) goes straight to its path. Chat is left
 * plain here because the room link is rendered on its own line underneath.
 */
const NotificationText = ({ item, text }: { item: NotificationItem; text: string }) => {
  if (item.task) return <TaskLink task={item.task} label={text} />;
  if (item.event) return <EventLink event={item.event} label={text} />;
  if (item.menu) return <MenuLink menu={item.menu} label={text} />;
  if (item.link && !item.chatRoom) return <PanelLink to={item.link}>{text}</PanelLink>;
  return <>{text}</>;
};

/** A link out of the panel — navigating always closes it behind you. */
const PanelLink = ({ to, children }: { to: string; children: ReactNode }) => {
  const { close: closePanel } = useNotificationsPanel();

  return (
    <Link to={to} className={styles.chatRoomLink} onClick={closePanel}>
      {children}
    </Link>
  );
};

const TaskLink = ({
  task,
  label,
}: {
  task: NonNullable<NotificationItem['task']>;
  label: string;
}) => {
  // The fetched task, not the notification's copy of it: the link's destination
  // depends on whether the task carries equipment, which the payload omits — and which
  // the server's own `/tasks/?task-id=` link cannot account for either.
  const { data: fetched } = useTaskLinkQuery(task.id);
  const text = label || task.title || 'task';

  return fetched ? <PanelLink to={taskHref(fetched)}>{text}</PanelLink> : <span>{text}</span>;
};

const EventLink = ({
  event,
  label,
}: {
  event: NonNullable<NotificationItem['event']>;
  label: string;
}) => {
  const { data: exists } = useEventExistsQuery(event.id);
  const text = label || event.title || 'event';

  return exists ? (
    <PanelLink to={`/events?${EVENT_ID_SEARCH_PARAM}=${event.id}`}>{text}</PanelLink>
  ) : (
    <span>{text}</span>
  );
};

/**
 * Links a menu notification to the event's menu page. Rendered as plain text when the
 * event is gone or the recipient can't open the menu (guests without menu access still
 * receive the notification, so the link would 403 on them).
 */
const MenuLink = ({
  menu,
  label,
}: {
  menu: NonNullable<NotificationItem['menu']>;
  label: string;
}) => {
  const { canAny } = usePermissions();
  const { data: exists } = useEventExistsQuery(menu.eventId);
  const canViewMenu = canAny([PERMISSIONS.menu.GetMenuInfo, PERMISSIONS.menu.EditMenu]);

  return exists && canViewMenu ? (
    <PanelLink to={`/event-menu/${menu.eventId}`}>{label}</PanelLink>
  ) : (
    <span>{label}</span>
  );
};

/**
 * The room a chat notification came from.
 *
 * Preferably linked off the loaded room list, which proves the reader can still open it. That
 * list is one page of the *current* service's rooms, though, so a room it does not name is not
 * necessarily gone — when the notification service sent a link for this event, that is the more
 * reliable answer and it wins over the empty check.
 */
const ChatRoomLink = ({
  room,
  href,
}: {
  room: NonNullable<NotificationItem['chatRoom']>;
  href?: string;
}) => {
  const chatRooms = useChats((s) => s.chatRooms);
  const exists = !!chatRooms?.some((r) => String(r.id) === room.id);
  const label = room.name || 'chat';
  const to = exists ? `/inbox/${room.id}` : href;

  return (
    <div className={styles.chatRoom}>
      <span>In </span>
      {to ? (
        <PanelLink to={to}>{label}</PanelLink>
      ) : (
        <span className={styles.chatRoomName}>{label}</span>
      )}
    </div>
  );
};

const InviteActions = ({ invite }: { invite: NonNullable<NotificationItem['invite']> }) => {
  const { invitation, accepted, accept, decline, pending } = useInviteResponse(
    invite.id,
    invite.serviceId,
  );
  const { expired, label } = useInviteExpiry(invite.expiresAt);

  const [declining, setDeclining] = useState(false);

  if (expired) return <div className={styles.description}>{label}</div>;
  if (!invitation) return null;

  return (
    <>
      {invite.additionalGuests > 0 && (
        <div className={styles.description}>
          <StatusPill label={`You + ${guestsLabel(invite.additionalGuests)}`} tone="info" />
        </div>
      )}
      {!accepted && label && <div className={styles.description}>{label}</div>}
      <div className={styles.inviteActions}>
        {!accepted && (
          <button
            type="button"
            className={clsx(styles.inviteButton, styles.inviteAccept)}
            onClick={(e) => {
              e.stopPropagation();
              void accept();
            }}
            disabled={pending}
          >
            Accept
          </button>
        )}
        <button
          type="button"
          className={clsx(styles.inviteButton, styles.inviteDecline)}
          onClick={(e) => {
            e.stopPropagation();
            setDeclining(true);
          }}
          disabled={pending}
        >
          Decline
        </button>
      </div>
      <DeclineReasonModal
        open={declining}
        title="Decline invitation"
        reasonPlaceholder="Let the sender know why you're declining"
        submitLabel="Decline"
        onClose={() => setDeclining(false)}
        onConfirm={(reason) => {
          setDeclining(false);
          decline(reason);
        }}
      />
    </>
  );
};
